import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Calendar,
  Users,
  Smartphone,
  Info,
  Search,
  MessageCircle,
  WifiOff,
  Mail,
} from 'lucide-react';
import Button from '../common/Button';

const UserGuideModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  const isFr = (i18n.language || 'fr').toLowerCase().startsWith('fr');

  const tipsTitle = isFr ? 'Conseils rapides' : 'Quick tips';
  const tips = isFr
    ? [
        {
          icon: Search,
          title: 'Recherche intelligente',
          desc: "Utilise la recherche et les filtres (auteur, categorie, disponibilite) pour aller plus vite. Pense a essayer plusieurs mots cles.",
        },
        {
          icon: MessageCircle,
          title: 'Reservation WhatsApp',
          desc: "Apres avoir ouvert la fiche d'un livre, clique sur le bouton WhatsApp et envoie un message avec le titre + l'auteur pour faciliter le traitement.",
        },
        {
          icon: Smartphone,
          title: "Installer l'application (PWA)",
          desc: "Sur Chrome/Edge: menu puis 'Ajouter a l'ecran d'accueil'. Sur iPhone: bouton Partager puis 'Sur l'ecran d'accueil'.",
        },
        {
          icon: WifiOff,
          title: 'Hors connexion',
          desc: "Certaines pages restent accessibles hors ligne. Si une page ne se charge pas, reviens a l'accueil et reessaie une fois connecte.",
        },
        {
          icon: Mail,
          title: "Besoin d'aide",
          desc: "Va dans la page Contact pour joindre l'equipe. Tu peux aussi signaler une erreur via le chat ou WhatsApp.",
        },
      ]
    : [
        {
          icon: Search,
          title: 'Smarter search',
          desc: 'Use search and filters (author, category, availability) to find books faster. Try different keywords.',
        },
        {
          icon: MessageCircle,
          title: 'WhatsApp reservation',
          desc: "Open a book details page, then use the WhatsApp button and include the book title + author in your message.",
        },
        {
          icon: Smartphone,
          title: 'Install the app (PWA)',
          desc: "On Chrome/Edge: menu then 'Add to Home screen'. On iPhone: Share then 'Add to Home Screen'.",
        },
        {
          icon: WifiOff,
          title: 'Offline mode',
          desc: 'Some pages stay available offline. If a page fails to load, go back to Home and retry when online.',
        },
        {
          icon: Mail,
          title: 'Need help',
          desc: 'Use the Contact page to reach the team. You can also report issues via chat or WhatsApp.',
        },
      ];

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white dark:bg-dark-surface rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-dark-surface px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                <Info className="h-6 w-6 text-primary-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                  {t('user_guide.title')}
                </h3>

                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('user_guide.intro')}</p>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <BookOpen className="h-5 w-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('user_guide.catalog.title')}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('user_guide.catalog.desc')}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <MessageCircle className="h-5 w-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('user_guide.reservation.title')}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('user_guide.reservation.desc')}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Users className="h-5 w-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('user_guide.groups.title')}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('user_guide.groups.desc')}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('user_guide.events.title')}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('user_guide.events.desc')}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Smartphone className="h-5 w-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">{t('user_guide.pwa.title')}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('user_guide.pwa.desc')}</p>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{tipsTitle}</h4>
                      <div className="space-y-3">
                        {tips.map((tip) => {
                          const TipIcon = tip.icon;
                          return (
                            <div key={tip.title} className="flex items-start">
                              <TipIcon className="h-5 w-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{tip.title}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{tip.desc}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <Button onClick={onClose} fullWidth className="sm:ml-3 sm:w-auto">
              {t('user_guide.close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

UserGuideModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UserGuideModal;

